import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { areIntervalsOverlapping, differenceInCalendarDays, eachDayOfInterval, endOfDay, isToday } from 'date-fns';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../forms/Button';
import { CalendarContext, CalendarEvent } from './Calendar';
import { Event } from './Event';
import { createTracks, denseArrangeTracks } from './util';

const useWeekViewClasses = makeStyles({
    root: {
        display: 'grid',
        gridAutoRows: 'max-content',
        gridTemplateColumns: 'repeat(7, 1fr)',
        alignItems: 'start',
        overflow: 'auto',
        flex: '1 0 0',
        '&.drag-over > div:not(.dragging), &.drag-over > span > button': { pointerEvents: 'none' },
    },
    cell: {
        display: 'contents',
        '& > span': {
            alignSelf: 'stretch',
            minHeight: 120,
            borderRight: `1px solid ${theme.colors.neutral[9]}`,
            borderBottom: `1px solid ${theme.colors.neutral[9]}`,
            mixBlendMode: 'multiply',
        },
        '& > button': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            flexDirection: 'column',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            gap: 4,
            borderRadius: 0,
            color: theme.colors.neutral[10],
            backgroundColor: 'white',
            mixBlendMode: 'normal',
            textTransform: 'uppercase',
        },
        '& > button > span:first-child': { ...theme.fonts.h4 },
        '& > button > span:nth-child(2)': { ...theme.fonts.h6 },
        '&.now > button': { color: theme.colors.darkBlue[5] },
        '&.drag-over > button': { boxShadow: `0 0 4px 2px ${theme.colors.neutral[3]} inset` },
    },
    row: {
        backgroundColor: theme.colors.neutral[9],
        height: '1px',
        pointerEvents: 'none',
    },
    event: {
        overflow: 'hidden',
        margin: 8,
    },
});

/**
 * Calendar week view.
 *
 * @param props.context calendar context
 */
export const WeekView = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useWeekViewClasses();

    const { t } = usePrefixTranslation('global', { global: 'calendar.WeekView' });
    const { events, intervals, onFilter, onSelected, onDnD, onExternalDnD } = React.useContext(props.context);
    const weekEvents = React.useMemo(
        () => events.filter(event => areIntervalsOverlapping(event.interval, intervals.week)),
        [events, +intervals.week.start, +intervals.week.end],
    );
    const weekTracks = React.useMemo(
        () =>
            denseArrangeTracks(
                createTracks(
                    weekEvents,
                    eachDayOfInterval(intervals.week).map(day => ({ start: day, end: endOfDay(day) })),
                ),
            ),
        [weekEvents],
    );
    const dragEvent = React.useRef<CalendarEvent>();

    React.useEffect(() => onFilter?.(weekEvents), [onFilter, weekEvents]);

    const cells = eachDayOfInterval(intervals.week).map((day, i) => {
        const buttonGridArea = `1/${1 + day.getDay()}/1/auto`;
        const cellGridArea = `1/${1 + day.getDay()}/${2 + weekTracks.levels}/auto`;
        const cls = { now: isToday(day) };
        return (
            <span
                key={`cell-${+day}`}
                className={clsx(classes.cell, cls)}
                onDragEnter={e => (dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')}
                onDragOver={e => (dragEvent.current || onExternalDnD) && e.preventDefault()}
                onDragLeave={e =>
                    e.target === e.currentTarget.children[0] && e.currentTarget.classList.remove('drag-over')
                }
                onDrop={e => {
                    e.currentTarget.classList.remove('drag-over');
                    if (!dragEvent.current) return onExternalDnD?.(e, day);
                    const difference = differenceInCalendarDays(day, dragEvent.current.interval.start);
                    if (difference !== 0) onDnD?.(dragEvent.current, { days: difference });
                }}
            >
                <span style={{ gridArea: cellGridArea }} />
                <Button style={{ gridArea: buttonGridArea }}>
                    <span>{day.getDate()}</span>
                    <span>{t('day', { date: day })}</span>
                </Button>
            </span>
        );
    });

    const rows = [...Array(weekTracks.levels)].map((_, i) => (
        <span key={i} className={classes.row} style={{ gridArea: `${2 + i}/1/auto/8` }} />
    ));

    const eventNodes = weekTracks.tracks.map((track, i) => {
        const event = track.events[0];
        const gridArea = `${2 + track.level}/${1 + track.ticks.start}/auto/${2 + track.ticks.end}`;
        return (
            <Event
                key={`event-${i}`}
                className={classes.event}
                style={{ gridArea }}
                event={event}
                view='week'
                onClick={() => onSelected?.(event)}
                draggable={event.draggable && !!onDnD}
                onDragStart={e => ((dragEvent.current = event), e.currentTarget.classList.add('dragging'))}
                onDragEnd={e => ((dragEvent.current = undefined), e.currentTarget.classList.remove('dragging'))}
            />
        );
    });

    return (
        <div
            className={clsx(classes.root, 'scroll-shadow')}
            style={{ ['--shadow-offset-t' as any]: '78px' }}
            onDragStart={e => (dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')}
            onDragEnter={e => (dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')}
            onDrop={e => e.currentTarget.classList.remove('drag-over')}
            onDragEnd={e => e.currentTarget.classList.remove('drag-over')}
        >
            {cells}
            {rows}
            {eventNodes}
        </div>
    );
};
